import { useState, useEffect, useRef } from 'react'
import ekkamLogo from '../../assets/Ekkam_Logo.png'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { useAppSelector, useAppDispatch } from '../../hooks/useAppDispatch'
import { logout } from '../../store/slices/authSlice'
import { useLoginPrompt } from '../../context/LoginPromptContext'
import { useOutsideClick } from '../../hooks/useOutsideClick'
import { useSSE } from '../../hooks/useSSE'
import { useGetDiscussionsQuery } from '../../store/api/discussionsApi'
import { useGetAiUsageQuery } from '../../store/api/usersApi'
import Avatar from '../ui/Avatar'
import { AiUsageModal } from './AiUsageModal'
import { api } from '../../lib/apiClient'
import { ENDPOINTS } from '../../lib/endpoints'
import { STORAGE_KEYS, DOM_EVENTS, NAV_RECENT_SEARCHES_MAX, NAV_TRENDING_COUNT, NAV_SEARCH_COUNT, SEARCH_DEBOUNCE_MS, ENABLE_DMS, ENABLE_SUPPORT, CHAT_RATE_LIMIT_PER_MIN } from '../../lib/config'

interface SearchResult {
  id: string
  title: string
  categories: { name: string; color: string | null; icon: string | null }[]
  commentCount: number
  createdAt: string
}

function NavSearch() {
  const navigate = useNavigate()
  const location = useLocation()
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<SearchResult[]>([])
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [recentSearches, setRecentSearches] = useState<string[]>(() => {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEYS.recentSearches) ?? '[]') as string[] }
    catch { return [] }
  })
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const justSubmittedRef = useRef(false)
  const wrapperRef = useOutsideClick<HTMLDivElement>(() => setOpen(false))

  const { data: trendingData, isLoading: trendingLoading } = useGetDiscussionsQuery(
    { sort: 'hot', limit: NAV_TRENDING_COUNT },
    { skip: !open }
  )
  const trending = (trendingData?.data ?? []) as SearchResult[]

  const saveRecentSearch = (q: string) => {
    const trimmed = q.trim()
    if (!trimmed) return
    const updated = [trimmed, ...recentSearches.filter((s) => s !== trimmed)].slice(0, NAV_RECENT_SEARCHES_MAX)
    setRecentSearches(updated)
    try { localStorage.setItem(STORAGE_KEYS.recentSearches, JSON.stringify(updated)) } catch {}
  }

  const removeRecentSearch = (q: string) => {
    const updated = recentSearches.filter((s) => s !== q)
    setRecentSearches(updated)
    try { localStorage.setItem(STORAGE_KEYS.recentSearches, JSON.stringify(updated)) } catch {}
  }

  const clearAllRecent = () => {
    setRecentSearches([])
    try { localStorage.removeItem(STORAGE_KEYS.recentSearches) } catch {}
  }

  useEffect(() => {
    if (location.pathname === '/search') {
      const q = new URLSearchParams(location.search).get('q') ?? ''
      setQuery(q)
    } else {
      setQuery(''); setResults([]); setOpen(false)
    }
  }, [location.pathname, location.search])

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    if (justSubmittedRef.current) { setResults([]); return }
    if (!query.trim()) { setResults([]); return }
    setOpen(true)
    if (query.trim().startsWith('#')) return
    debounceRef.current = setTimeout(async () => {
      setLoading(true)
      try {
        const d = await api.get<{ data: SearchResult[] }>('discussions', `${ENDPOINTS.discussions.list}?search=${encodeURIComponent(query.trim())}&limit=${NAV_SEARCH_COUNT}`)
        setResults(d.data ?? [])
      } catch {}
      setLoading(false)
    }, SEARCH_DEBOUNCE_MS)
  }, [query])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const trimmed = query.trim()
    if (!trimmed) return
    saveRecentSearch(trimmed)
    if (debounceRef.current) clearTimeout(debounceRef.current)
    justSubmittedRef.current = true
    setQuery('')
    setResults([])
    setOpen(false)
    inputRef.current?.blur()
    if (trimmed.startsWith('#')) {
      const slug = trimmed.slice(1).toLowerCase().replace(/\s+/g, '-')
      if (slug) void navigate(`/search?tag=${encodeURIComponent(slug)}`)
    } else {
      void navigate(`/search?q=${encodeURIComponent(trimmed)}`)
    }
  }

  const handleSelect = (id: string) => {
    saveRecentSearch(query); setOpen(false); setQuery(''); setResults([])
    void navigate(`/discussions/${id}`)
  }

  const handleRecentClick = (q: string) => {
    saveRecentSearch(q); setOpen(false)
    void navigate(`/search?q=${encodeURIComponent(q)}`)
  }

  const handleClear = () => {
    setQuery(''); setResults([]); setOpen(false)
    if (location.pathname === '/search') void navigate('/')
  }

  return (
    <div ref={wrapperRef} className="nav-search" style={{ position: 'relative', width: '100%' }}>
      <form onSubmit={handleSubmit} style={{ width: '100%' }}>
        <div style={{ position: 'relative' }}>
          <span style={{ position: 'absolute', left: '0.85rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', fontSize: '0.8rem', pointerEvents: 'none' }}>🔍</span>
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => { justSubmittedRef.current = false; setQuery(e.target.value) }}
            onFocus={() => { justSubmittedRef.current = false; setOpen(true) }}
            placeholder="Search discussions..."
            style={{ width: '100%', padding: '0.5rem 2rem 0.5rem 2.25rem', background: 'var(--bg-base)', border: '1px solid var(--border)', borderRadius: '20px', color: 'var(--text-primary)', fontSize: '0.875rem', outline: 'none', boxSizing: 'border-box' }}
          />
          {loading && <span style={{ position: 'absolute', right: '0.85rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', fontSize: '0.72rem' }}>...</span>}
          {!loading && query && (
            <button type="button" onClick={handleClear} style={{ position: 'absolute', right: '0.6rem', top: '50%', transform: 'translateY(-50%)', background: 'var(--bg-elevated)', border: 'none', borderRadius: '50%', width: '18px', height: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'var(--text-muted)', fontSize: '0.65rem', padding: 0, lineHeight: 1 }}>✕</button>
          )}
        </div>
      </form>

      {open && (
        <div style={{ position: 'absolute', top: 'calc(100% + 6px)', left: 0, right: 0, background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '12px', boxShadow: '0 8px 24px rgba(0,0,0,0.3)', zIndex: 200, overflow: 'hidden' }}>
          {!query.trim() && (
            <>
              {recentSearches.length > 0 && (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem 0.875rem 0.25rem' }}>
                    <span style={{ fontSize: '0.7rem', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Recent</span>
                    <button onClick={clearAllRecent} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '0.72rem', padding: '0.1rem 0.25rem' }}>Clear all</button>
                  </div>
                  {recentSearches.map((q) => (
                    <div key={q} style={{ display: 'flex', alignItems: 'center', gap: '0.65rem', padding: '0.5rem 0.875rem', cursor: 'pointer', transition: 'background 0.1s' }} onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.background = 'var(--bg-elevated)' }} onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.background = 'transparent' }}>
                      <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)', flexShrink: 0 }}>🕐</span>
                      <span onClick={() => handleRecentClick(q)} style={{ flex: 1, fontSize: '0.85rem', color: 'var(--text-secondary)' }}>{q}</span>
                      <button onClick={(e) => { e.stopPropagation(); removeRecentSearch(q) }} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '0.7rem', padding: '0.1rem 0.25rem', flexShrink: 0, lineHeight: 1 }}>✕</button>
                    </div>
                  ))}
                  <div style={{ height: '1px', background: 'var(--border)', margin: '0.25rem 0' }} />
                </>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem 0.875rem 0.25rem' }}>
                <span style={{ fontSize: '0.7rem', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>🔥 Trending</span>
              </div>
              {trendingLoading && <div style={{ padding: '0.75rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.82rem' }}>Loading...</div>}
              {!trendingLoading && trending.map((r) => (
                <div key={r.id} onClick={() => handleSelect(r.id)} style={{ display: 'flex', alignItems: 'center', gap: '0.65rem', padding: '0.55rem 0.875rem', cursor: 'pointer', borderBottom: '1px solid var(--border)', transition: 'background 0.1s' }} onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.background = 'var(--bg-elevated)' }} onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.background = 'transparent' }}>
                  <span style={{ fontSize: '1rem', flexShrink: 0 }}>{r.categories[0]?.icon ?? '🔥'}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '0.85rem', fontWeight: 500, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.title}</div>
                    <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '0.1rem' }}>
                      <span style={{ color: r.categories[0]?.color ?? 'var(--accent-primary)' }}>{r.categories[0]?.name}</span>
                      <span style={{ margin: '0 0.3rem' }}>·</span>
                      {r.commentCount} {r.commentCount === 1 ? 'reply' : 'replies'}
                    </div>
                  </div>
                </div>
              ))}
            </>
          )}
          {query.trim() && (
            <>
              {query.trim().startsWith('#') && (
                <div
                  onClick={() => {
                    const slug = query.trim().slice(1).toLowerCase().replace(/\s+/g, '-')
                    if (slug) { saveRecentSearch(query.trim()); setOpen(false); void navigate(`/search?tag=${encodeURIComponent(slug)}`) }
                  }}
                  style={{ display: 'flex', alignItems: 'center', gap: '0.65rem', padding: '0.75rem 0.875rem', cursor: 'pointer', transition: 'background 0.1s' }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.background = 'var(--bg-elevated)' }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.background = 'transparent' }}
                >
                  <span style={{ fontSize: '1rem', flexShrink: 0 }}>#️⃣</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--accent-primary)' }}>
                      Search by tag: <span style={{ color: 'var(--text-primary)' }}>{query.trim().slice(1)}</span>
                    </div>
                    <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: '0.1rem' }}>Press Enter to see all discussions with this tag</div>
                  </div>
                </div>
              )}
              {!query.trim().startsWith('#') && (
                <>
                  {loading && <div style={{ padding: '0.75rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.82rem' }}>Searching...</div>}
                  {!loading && results.length === 0 && <div style={{ padding: '1rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.85rem' }}>No results for "{query}"</div>}
                  {results.map((r) => (
                    <div key={r.id} onClick={() => handleSelect(r.id)} style={{ display: 'flex', alignItems: 'center', gap: '0.65rem', padding: '0.65rem 0.875rem', cursor: 'pointer', borderBottom: '1px solid var(--border)', transition: 'background 0.1s' }} onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.background = 'var(--bg-elevated)' }} onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.background = 'transparent' }}>
                      <span style={{ fontSize: '1rem', flexShrink: 0 }}>{r.categories[0]?.icon ?? '💬'}</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '0.85rem', fontWeight: 500, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.title}</div>
                        <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '0.1rem' }}>
                          <span style={{ color: r.categories[0]?.color ?? 'var(--accent-primary)' }}>{r.categories[0]?.name}</span>
                          <span style={{ margin: '0 0.3rem' }}>·</span>
                          {r.commentCount} {r.commentCount === 1 ? 'reply' : 'replies'}
                        </div>
                      </div>
                    </div>
                  ))}
                  {!loading && (
                    <div
                      onClick={() => { saveRecentSearch(query); setOpen(false); void navigate(`/search?q=${encodeURIComponent(query.trim())}`) }}
                      style={{ padding: '0.6rem 0.875rem', textAlign: 'center', fontSize: '0.8rem', color: 'var(--accent-primary)', cursor: 'pointer', fontWeight: 500 }}
                      onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.background = 'var(--bg-elevated)' }}
                      onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.background = 'transparent' }}
                    >
                      See all results for "{query}" →
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      )}
    </div>
  )
}

function CreatorUserToggle() {
  const location = useLocation()
  const navigate = useNavigate()
  const isCreator = location.pathname.startsWith('/developer')

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        background: 'var(--bg-elevated)',
        border: '1px solid var(--border)',
        borderRadius: '8px',
        padding: '2px',
        gap: '2px',
        flexShrink: 0,
      }}
    >
      <button
        onClick={() => void navigate('/')}
        title="User view"
        style={{
          display: 'flex', alignItems: 'center', gap: '0.3rem',
          padding: '0.25rem 0.6rem',
          borderRadius: '6px',
          border: 'none',
          fontSize: '0.75rem',
          fontWeight: 600,
          cursor: 'pointer',
          transition: 'all 0.15s',
          whiteSpace: 'nowrap',
          background: !isCreator ? 'var(--bg-card)' : 'transparent',
          color: !isCreator ? 'var(--text-primary)' : 'var(--text-muted)',
          boxShadow: !isCreator ? '0 1px 3px rgba(0,0,0,0.2)' : 'none',
        }}
      >
        <span>👤</span>
        <span className="nav-toggle-label">User</span>
      </button>
      <button
        onClick={() => void navigate('/developer')}
        title="Creator Portal"
        style={{
          display: 'flex', alignItems: 'center', gap: '0.3rem',
          padding: '0.25rem 0.6rem',
          borderRadius: '6px',
          border: 'none',
          fontSize: '0.75rem',
          fontWeight: 600,
          cursor: 'pointer',
          transition: 'all 0.15s',
          whiteSpace: 'nowrap',
          background: isCreator ? 'var(--accent-primary)' : 'transparent',
          color: isCreator ? 'white' : 'var(--text-muted)',
          boxShadow: isCreator ? '0 1px 3px rgba(0,0,0,0.3)' : 'none',
        }}
      >
        <span>🛠️</span>
        <span className="nav-toggle-label">Creator</span>
      </button>
    </div>
  )
}

function usageBarMeta(entry: { used: number; limit: number; remaining: number; resetAt: string }, tokenBased: boolean, warnCount: number) {
  const pct = Math.min(100, (entry.used / entry.limit) * 100)
  const isWarning = entry.remaining <= (tokenBased ? entry.limit * 0.1 : warnCount) && entry.remaining > 0
  const isDepleted = entry.remaining === 0
  const color = isDepleted ? 'var(--error)' : isWarning ? 'var(--warning)' : 'var(--accent-primary)'
  const barBg = isDepleted ? 'rgba(239,68,68,0.15)' : isWarning ? 'rgba(249,115,22,0.1)' : 'var(--bg-base)'
  const resetStr = new Date(entry.resetAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true })
  return { pct, isWarning, isDepleted, color, barBg, resetStr }
}

function UsagePopoverRow({ icon, kindLabel, entry, tokenBased, warnCount }: {
  icon: string; kindLabel: string
  entry: { used: number; limit: number; remaining: number; resetAt: string }
  tokenBased: boolean; warnCount: number
}) {
  const { pct, isDepleted, color, barBg, resetStr } = usageBarMeta(entry, tokenBased, warnCount)
  const label = isDepleted
    ? `${kindLabel} limit reached`
    : tokenBased ? `${Math.round(pct)}% used` : `${entry.used} / ${entry.limit} requests`
  return (
    <div style={{ padding: '0.4rem 0' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.3rem', gap: '0.5rem' }}>
        <span style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-primary)', whiteSpace: 'nowrap' }}>{icon} {kindLabel}</span>
        <span style={{ fontSize: '0.72rem', fontWeight: 600, color, whiteSpace: 'nowrap' }}>{label}</span>
      </div>
      <div style={{ width: '100%', height: '5px', background: barBg, borderRadius: '3px', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: '3px', transition: 'width 0.4s ease' }} />
      </div>
      {entry.used > 0 && (
        <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>Resets {resetStr}</div>
      )}
    </div>
  )
}

export default function TopNav() {
  const { isAuthenticated, user, isRetrying, accessToken } = useAppSelector((s) => s.auth)
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const isOnChatPage = location.pathname === '/chat' || location.pathname.startsWith('/chat/')
  const { trigger: triggerLoginPrompt } = useLoginPrompt()
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [usagePopoverOpen, setUsagePopoverOpen] = useState(false)
  const [usageModalOpen, setUsageModalOpen] = useState(false)

  const { data: aiUsageData, refetch: refetchAiUsage } = useGetAiUsageQuery(undefined, {
    skip: !isAuthenticated,
    pollingInterval: 60000,
  })

  const refetchAiUsageRef = useRef(refetchAiUsage)
  useEffect(() => { refetchAiUsageRef.current = refetchAiUsage }, [refetchAiUsage])

  const [chatRate, setChatRate] = useState<{ count: number; limit: number }>({ count: 0, limit: CHAT_RATE_LIMIT_PER_MIN })
  useEffect(() => {
    const handler = (e: Event) => {
      const { count, limit } = (e as CustomEvent<{ count: number; limit: number }>).detail
      setChatRate(count === -1 ? { count: 0, limit } : { count, limit })
    }
    window.addEventListener(DOM_EVENTS.chatRate, handler)
    return () => window.removeEventListener(DOM_EVENTS.chatRate, handler)
  }, [])

  useSSE({
    enabled: isAuthenticated,
    onEvent: {
      notification: () => { window.dispatchEvent(new Event(DOM_EVENTS.notification)) },
      ai_usage_updated: () => { void refetchAiUsageRef.current() },
    },
  })

  const handleLogout = async () => {
    try { await api.post('auth', ENDPOINTS.auth.logout, undefined, accessToken) } catch {}
    dispatch(logout())
    setDropdownOpen(false)
    void navigate('/')
  }

  return (
    <>
      {isRetrying && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, height: '3px', background: 'linear-gradient(90deg, var(--accent-primary), #06b6d4, var(--accent-primary))', backgroundSize: '200% 100%', animation: 'reconnect 1.5s linear infinite', zIndex: 200 }}>
          <style>{`@keyframes reconnect { 0% { background-position: 200% 0 } 100% { background-position: -200% 0 } }`}</style>
        </div>
      )}

      <nav className="topnav">
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexShrink: 0 }}>
          <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', textDecoration: 'none', flexShrink: 0 }}>
            <img src={ekkamLogo} alt="Ekkam AI" style={{ height: '28px', width: 'auto', objectFit: 'contain', display: 'block' }} />
          </Link>
          <Link to="/leaderboard" className="nav-mobile-only" title="Leaderboard"
            style={{ display: 'none', alignItems: 'center', justifyContent: 'center', fontSize: '1.2rem', textDecoration: 'none', flexShrink: 0 }}>
            🏆
          </Link>
          <Link to="/leaderboard" className="nav-desktop-only"
            style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', textDecoration: 'none', padding: '0.3rem 0.65rem', borderRadius: '8px', border: '1px solid var(--border)', fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-muted)', transition: 'all 0.15s', whiteSpace: 'nowrap' }}
            onMouseEnter={e => { const el = e.currentTarget as HTMLAnchorElement; el.style.color = 'var(--warning)'; el.style.borderColor = '#eab30844'; el.style.background = '#eab30810' }}
            onMouseLeave={e => { const el = e.currentTarget as HTMLAnchorElement; el.style.color = 'var(--text-muted)'; el.style.borderColor = 'var(--border)'; el.style.background = 'transparent' }}>
            <span>🏆</span><span className="nav-toggle-label" style={{ marginLeft: '0.3rem' }}>Leaderboard</span>
          </Link>
          {isAuthenticated && (
            <CreatorUserToggle />
          )}
        </div>

        <NavSearch />

        <div style={{ justifySelf: 'end', display: 'flex', alignItems: 'center', gap: '0.75rem', flexShrink: 0 }}>
          {isAuthenticated && user ? (
            <>
              {/* AI Usage indicators — click to see full breakdown */}
              {aiUsageData && (
                <span className="nav-desktop-only" style={{ display: 'contents' }}>
                <div style={{ position: 'relative' }}>
                  {!isOnChatPage && (() => {
                    const disc = aiUsageData.discussion
                    const { pct, isWarning, isDepleted, color, barBg, resetStr } = usageBarMeta(disc, aiUsageData.tokenBased, 2)
                    const label = isDepleted ? 'Discussion limit reached' : aiUsageData.tokenBased ? `${Math.round(pct)}% used` : `${disc.used} / ${disc.limit} requests`
                    return (
                      <button onClick={() => setUsagePopoverOpen(v => !v)}
                        style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.3rem 0.7rem', borderRadius: '8px', background: isDepleted ? 'rgba(239,68,68,0.06)' : isWarning ? 'rgba(249,115,22,0.06)' : 'var(--bg-elevated)', border: `1px solid ${isDepleted ? 'rgba(239,68,68,0.2)' : isWarning ? 'rgba(249,115,22,0.2)' : 'var(--border)'}`, cursor: 'pointer', transition: 'all 0.2s', flexShrink: 0 }}>
                        <span style={{ fontSize: '0.8rem', flexShrink: 0 }}>🤖</span>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', minWidth: '80px' }}>
                          <div style={{ fontSize: '0.68rem', fontWeight: 600, color, lineHeight: 1, whiteSpace: 'nowrap' }}>{label}</div>
                          <div style={{ width: '100%', height: '3px', background: barBg, borderRadius: '3px', overflow: 'hidden' }}>
                            <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: '3px', transition: 'width 0.4s ease' }} />
                          </div>
                        </div>
                        {isDepleted && <span style={{ fontSize: '0.62rem', color: 'var(--error)', fontWeight: 500, whiteSpace: 'nowrap', opacity: 0.8 }}>Resets {resetStr}</span>}
                      </button>
                    )
                  })()}
                  {isOnChatPage && (() => {
                    const chat = aiUsageData.chat
                    const { pct, isWarning, isDepleted, color, barBg, resetStr } = usageBarMeta(chat, aiUsageData.tokenBased, 5)
                    const label = isDepleted ? 'Chat limit reached' : aiUsageData.tokenBased ? `${Math.round(pct)}% used` : `${chat.used} / ${chat.limit} requests`
                    return (
                      <button onClick={() => setUsagePopoverOpen(v => !v)}
                        style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.3rem 0.7rem', borderRadius: '8px', background: isDepleted ? 'rgba(239,68,68,0.06)' : isWarning ? 'rgba(249,115,22,0.06)' : 'var(--bg-elevated)', border: `1px solid ${isDepleted ? 'rgba(239,68,68,0.2)' : isWarning ? 'rgba(249,115,22,0.2)' : 'var(--border)'}`, cursor: 'pointer', transition: 'all 0.2s', flexShrink: 0 }}>
                        <span style={{ fontSize: '0.8rem', flexShrink: 0 }}>💬</span>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', minWidth: '80px' }}>
                          <div style={{ fontSize: '0.68rem', fontWeight: 600, color, lineHeight: 1, whiteSpace: 'nowrap' }}>{label}</div>
                          <div style={{ width: '100%', height: '3px', background: barBg, borderRadius: '3px', overflow: 'hidden' }}>
                            <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: '3px', transition: 'width 0.4s ease' }} />
                          </div>
                        </div>
                        {isDepleted && <span style={{ fontSize: '0.62rem', color: 'var(--error)', fontWeight: 500, whiteSpace: 'nowrap', opacity: 0.8 }}>Resets {resetStr}</span>}
                      </button>
                    )
                  })()}

                  {usagePopoverOpen && (
                    <>
                      <div onClick={() => setUsagePopoverOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 9998 }} />
                      <div style={{ position: 'absolute', top: 'calc(100% + 6px)', left: 0, background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '10px', padding: '0.85rem 1rem', minWidth: '240px', boxShadow: 'var(--shadow-elevated)', zIndex: 9999 }}>
                        <div style={{ fontWeight: 700, fontSize: '0.82rem', color: 'var(--text-primary)', marginBottom: '0.2rem' }}>🤖 AI Usage Today</div>
                        <UsagePopoverRow icon="🗨️" kindLabel="Discussions" entry={aiUsageData.discussion} tokenBased={aiUsageData.tokenBased} warnCount={2} />
                        <div style={{ borderTop: '1px solid var(--border)', margin: '0.2rem 0' }} />
                        <UsagePopoverRow icon="💬" kindLabel="Chat" entry={aiUsageData.chat} tokenBased={aiUsageData.tokenBased} warnCount={5} />
                        <button onClick={() => { setUsagePopoverOpen(false); setUsageModalOpen(true) }}
                          style={{ display: 'block', width: '100%', textAlign: 'left', marginTop: '0.5rem', paddingTop: '0.5rem', borderTop: '1px solid var(--border)', background: 'transparent', borderLeft: 'none', borderRight: 'none', borderBottom: 'none', fontSize: '0.75rem', fontWeight: 600, color: 'var(--accent-primary)', cursor: 'pointer' }}>
                          View full usage breakdown →
                        </button>
                      </div>
                    </>
                  )}
                </div>
                </span>
              )}
              {ENABLE_DMS && (
                <Link to="/chat" title="AI Chat"
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '34px', height: '34px', borderRadius: '8px', background: 'transparent', border: '1px solid var(--border)', color: 'var(--text-muted)', textDecoration: 'none', fontSize: '1rem', transition: 'all 0.15s', flexShrink: 0 }}
                  onMouseEnter={(e) => { const el = e.currentTarget as HTMLAnchorElement; el.style.color = 'var(--accent-primary)'; el.style.borderColor = 'var(--accent-primary)'; el.style.background = 'var(--accent-light)' }}
                  onMouseLeave={(e) => { const el = e.currentTarget as HTMLAnchorElement; el.style.color = 'var(--text-muted)'; el.style.borderColor = 'var(--border)'; el.style.background = 'transparent' }}>
                  💬
                </Link>
              )}
              {/* Desktop dropdown */}
              <span className="nav-desktop-only" style={{ display: 'contents' }}>
                <div style={{ position: 'relative' }}>
                  <button onClick={() => setDropdownOpen(!dropdownOpen)} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', background: 'transparent', border: 'none', cursor: 'pointer' }}>
                    <Avatar name={user.displayName} avatarUrl={user.avatarUrl} size={32} />
                  </button>
                  {dropdownOpen && (
                    <>
                      <div onClick={() => setDropdownOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 9998 }} />
                      <div style={{ position: 'absolute', top: '40px', right: 0, background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '8px', padding: '0.5rem', minWidth: '180px', boxShadow: 'var(--shadow-elevated)', zIndex: 9999 }}>
                        <div style={{ padding: '0.5rem', borderBottom: '1px solid var(--border)', marginBottom: '0.5rem' }}>
                          <div style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: '0.875rem' }}>{user.displayName}</div>
                        </div>
                        <DropdownItem to="/profile"   label="👤 Profile"          onClick={() => setDropdownOpen(false)} />
                        <DropdownItem to="/following" label="🔔 Following"         onClick={() => setDropdownOpen(false)} />
                        <DropdownItem to="/settings"  label="⚙️ Settings"          onClick={() => setDropdownOpen(false)} />
                        <DropdownItem to="/billing"   label="🧾 Billing"           onClick={() => setDropdownOpen(false)} />

                        {user.roles.includes('admin') && <DropdownItem to="/admin" label="🛡️ Admin Panel" onClick={() => setDropdownOpen(false)} />}
                        <div style={{ borderTop: '1px solid var(--border)', marginTop: '0.5rem', paddingTop: '0.5rem' }}>
                          {ENABLE_SUPPORT && <DropdownItem to="/support" label="❓ Help & Support" onClick={() => setDropdownOpen(false)} />}
                          <button onClick={() => void handleLogout()} style={{ width: '100%', textAlign: 'left', padding: '0.5rem 0.75rem', background: 'transparent', border: 'none', color: 'var(--error)', cursor: 'pointer', fontSize: '0.875rem', borderRadius: '6px', transition: 'background 0.1s' }}
                            onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--bg-elevated)' }}
                            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent' }}>
                            🚪 Sign out
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </span>
              {/* Mobile dropdown */}
              <div className="nav-mobile-only" style={{ display: 'none', position: 'relative' }}>
                <button onClick={() => setDropdownOpen(!dropdownOpen)} style={{ display: 'flex', alignItems: 'center', background: 'transparent', border: 'none', cursor: 'pointer', padding: 0 }}>
                  <Avatar name={user.displayName} avatarUrl={user.avatarUrl} size={32} />
                </button>
                {dropdownOpen && (
                  <>
                    <div onClick={() => setDropdownOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 9998 }} />
                    <div style={{ position: 'fixed', top: '56px', right: '0.75rem', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '10px', padding: '0.5rem', minWidth: '180px', boxShadow: 'var(--shadow-elevated)', zIndex: 9999 }}>
                      <div style={{ padding: '0.5rem 0.75rem', borderBottom: '1px solid var(--border)', marginBottom: '0.25rem' }}>
                        <div style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: '0.875rem' }}>{user.displayName}</div>
                      </div>
                      <DropdownItem to="/profile"   label="👤 Profile"          onClick={() => setDropdownOpen(false)} />
                      <DropdownItem to="/following" label="🔔 Following"         onClick={() => setDropdownOpen(false)} />
                      <DropdownItem to="/settings"  label="⚙️ Settings"          onClick={() => setDropdownOpen(false)} />
                      <DropdownItem to="/billing"   label="🧾 Billing"           onClick={() => setDropdownOpen(false)} />

                      {user.roles.includes('admin') && <DropdownItem to="/admin" label="🛡️ Admin" onClick={() => setDropdownOpen(false)} />}
                      <div style={{ borderTop: '1px solid var(--border)', marginTop: '0.25rem', paddingTop: '0.25rem' }}>
                        {ENABLE_SUPPORT && <DropdownItem to="/support" label="❓ Help & Support" onClick={() => setDropdownOpen(false)} />}
                        <button onClick={() => void handleLogout()} style={{ width: '100%', textAlign: 'left', padding: '0.5rem 0.75rem', background: 'transparent', border: 'none', color: 'var(--error)', cursor: 'pointer', fontSize: '0.875rem', borderRadius: '6px', transition: 'background 0.1s' }}
                          onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--bg-elevated)' }}
                          onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent' }}>
                          🚪 Sign out
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </>
          ) : (
            <>
              <span className="nav-desktop-only">
                <button onClick={() => triggerLoginPrompt()} style={{ background: 'var(--accent-gradient)', color: 'white', padding: '0.5rem 1rem', borderRadius: '20px', border: 'none', fontWeight: 600, fontSize: '0.875rem', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                  Sign in
                </button>
              </span>
              <button onClick={() => triggerLoginPrompt()} className="nav-mobile-only" style={{ display: 'none', background: 'var(--accent-gradient)', color: 'white', padding: '0.4rem 0.875rem', borderRadius: '20px', border: 'none', fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                Sign in
              </button>
            </>
          )}
        </div>
      </nav>
      {usageModalOpen && <AiUsageModal onClose={() => setUsageModalOpen(false)} />}
    </>
  )
}

const DropdownItem = ({ to, label, onClick }: { to: string; label: string; onClick: () => void }) => {
  const navigate = useNavigate()
  return (
    <button
      onClick={() => { onClick(); void navigate(to) }}
      style={{ display: 'block', width: '100%', textAlign: 'left', padding: '0.5rem 0.75rem', color: 'var(--text-secondary)', background: 'transparent', border: 'none', cursor: 'pointer', fontSize: '0.875rem', borderRadius: '6px' }}
      onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--bg-elevated)' }}
      onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent' }}
    >
      {label}
    </button>
  )
}
